import { fetchPendingTokens, updateTokenStatus } from '../../services/tokenApi.js';

const formatTokenTime = (dateString) => {
  if (!dateString) return '---';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '---';
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

export const createStaffOrdersSlice = (set, get) => ({
  // Orders/Tokens state
  orders: [],
  completedOrders: [],
  loading: false,
  error: null,

  // Initialize pending tokens
  initializePendingTokens: async () => {
    set({ loading: true, error: null });
    try {
      const response = await fetchPendingTokens();
      if (response.success) {
        // Transform tokens to orders format and ignore cancelled tokens
        const transformedOrders = response.tokens
          .filter((token) => token.status !== 'cancelled')
          .map((token) => ({
            id: token.order_id,
            // Normalize token value from various possible backend shapes
            token: `${token.token_no ?? token.token_number ?? token.token ?? token.id ?? ''}`,
            time: formatTokenTime(token.created_at),
            createdAt: new Date(token.created_at),
            items: token.items.map((item) => ({
              name: item.product_name,
              quantity: item.quantity
            })),
            category: token.order_type === 'table' ? 'Tables' : 'Token Queue',
            status: token.status,
            tokenId: token.id,
            tableName: token.table_name
          }));
        set({ orders: transformedOrders, loading: false });
      }
    } catch (error) {
      console.error('Error initializing pending tokens:', error);
      set({ error: error.message, loading: false });
    }
  },

  addOrder: (order) => set((state) => ({
    orders: [order, ...state.orders]
  })),

  completeOrder: async (orderId) => {
    try {
      await updateTokenStatus(orderId, 'completed');
      set((state) => {
        const order = state.orders.find(o => o.id === orderId);
        if (!order) return state;

        return {
          orders: state.orders.filter(o => o.id !== orderId),
          completedOrders: [...state.completedOrders, { ...order, status: 'completed' }]
        };
      });
    } catch (error) {
      console.error('Error completing order:', error);
      set((state) => ({ error: error.message }));
    }
  },

  completeAllOrders: async () => {
    const { orders } = get();
    const visibleOrders = orders.filter(order => order.category === 'Token Queue' && order.status !== 'cancelled');
    if (visibleOrders.length === 0) {
      return;
    }

    try {
      const results = await Promise.allSettled(
        visibleOrders.map(order => updateTokenStatus(order.id, 'completed'))
      );

      const successfulOrderIds = visibleOrders
        .filter((order, index) => results[index].status === 'fulfilled')
        .map(order => order.id);
      const failedResults = results
        .map((result, index) => ({ result, order: visibleOrders[index] }))
        .filter(item => item.result.status === 'rejected');

      set((state) => ({
        orders: state.orders.filter(order => !successfulOrderIds.includes(order.id)),
        completedOrders: [
          ...state.completedOrders,
          ...visibleOrders
            .filter((order) => successfulOrderIds.includes(order.id))
            .map((order) => ({ ...order, status: 'completed' }))
        ],
        error: failedResults.length ? failedResults.map((item) => item.result.reason?.message || 'Failed to complete order').join('; ') : null
      }));

      if (failedResults.length > 0) {
        throw new Error(`${failedResults.length} order(s) failed to complete`);
      }
    } catch (error) {
      console.error('Error completing all orders:', error);
      set((state) => ({ error: error.message }));
    }
  },

  // Real-time updates from socket
  updateTokenFromSocket: (tokenData) => {
    set((state) => {
      const parsedOrderId = Number(tokenData.order_id);
      // Check if token already exists
      const existingIndex = state.orders.findIndex(o => o.id === parsedOrderId);

      if (existingIndex > -1) {
        const existing = state.orders[existingIndex];
        const updated = [...state.orders];
        updated[existingIndex] = {
          ...existing,
          status: tokenData.status || existing.status || 'issued',
          time: tokenData.created_at ? formatTokenTime(tokenData.created_at) : existing.time,
          createdAt: tokenData.created_at ? new Date(tokenData.created_at) : existing.createdAt,
          items: tokenData.items && tokenData.items.length > 0 ? tokenData.items : existing.items,
          token: tokenData.token ? `${tokenData.token.token_no ?? tokenData.token.token_number ?? tokenData.token ?? tokenData.token.id ?? ''}` : existing.token,
          tokenId: tokenData.token?.id || existing.tokenId,
          tableName: tokenData.table_name || existing.tableName
        };
        return { orders: updated };
      } else {
        // Add new token
        const newOrder = {
          id: tokenData.order_id,
          token: `${tokenData.token?.token_no ?? tokenData.token?.token_number ?? tokenData.token ?? tokenData.token?.id ?? ''}`,
          time: formatTokenTime(tokenData.created_at),
          createdAt: tokenData.created_at ? new Date(tokenData.created_at) : new Date(),
          items: tokenData.items || [],
          category: tokenData.order_type === 'table' ? 'Tables' : 'Token Queue',
          status: tokenData.status || 'issued',
          tokenId: tokenData.token.id,
          tableName: tokenData.table_name
        };
        return { orders: [newOrder, ...state.orders] };
      }
    });
  },

  removeCompletedToken: (orderId, eventStatus = 'completed') => {
    set((state) => {
      const order = state.orders.find(o => o.id === orderId);
      if (!order) return state;

      return {
        orders: state.orders.filter(o => o.id !== orderId),
        completedOrders: [...state.completedOrders, { ...order, status: eventStatus === 'cancelled' ? 'cancelled' : 'completed' }]
      };
    });
  },

  // Getters
  getVisibleOrders: () => {
    const { orders } = get();
    return orders
      .filter(order => order.category === 'Token Queue' && order.status !== 'cancelled')
      .sort((a, b) => a.createdAt - b.createdAt);
  },

  getActiveOrderCount: () => {
    return get().getVisibleOrders().length;
  }
});